import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, LogIn, UserPlus } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Acceso concedido");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error de acceso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[var(--bg)]" data-testid="login-page">
      {/* Left: Brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-[var(--border)] bg-[var(--surface)] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <BrandMark className="w-10 h-10" />
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none">AGENT COMPANY OS</div>
              <div className="label-mono mt-1">v1.0 · Command Center</div>
            </div>
          </div>

          <h1 className="font-display font-black text-5xl leading-[0.95] tracking-tight mb-6">
            Empresa autónoma<br />
            <span className="text-[var(--accent)]">con permiso.</span>
          </h1>
          <p className="text-[var(--text-dim)] max-w-md leading-relaxed">
            20 agentes master por departamento. RAG, auditoría inmutable, kill-switches
            y aprobación humana en cada decisión sensible.
          </p>
        </div>

        <div className="relative z-10 space-y-3">
          {[
            { code: "OWASP LLM Top 10", desc: "Prompt injection, excessive agency" },
            { code: "NIST AI RMF", desc: "Gobernanza y trazabilidad" },
            { code: "ISO/IEC 42001", desc: "Sistema de gestión de IA" },
            { code: "EU AI Act", desc: "Riesgo, transparencia, supervisión" },
          ].map((f) => (
            <div key={f.code} className="flex items-center gap-3 py-2 border-t border-[var(--border)]">
              <ShieldCheck className="w-4 h-4 text-[var(--good)]" strokeWidth={2} />
              <div className="flex-1 flex items-baseline justify-between gap-4">
                <span className="font-mono text-xs uppercase text-white">{f.code}</span>
                <span className="text-xs text-[var(--text-dim)]">{f.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="label-mono mb-3">ACCESO OPERATIVO</div>
            <h2 className="font-display font-bold text-3xl tracking-tight mb-2">Inicia sesión</h2>
            <p className="text-sm text-[var(--text-dim)]">
              Solo humanos autorizados. Cada acceso queda auditado.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label className="label-mono">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                className="rounded-none bg-[var(--surface)] border-[var(--border)] h-11 font-mono"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-mono">Contraseña</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none bg-[var(--surface)] border-[var(--border)] h-11 font-mono"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none h-11 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold"
              data-testid="login-submit"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {loading ? "Autenticando..." : "Entrar"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[var(--border)] text-center">
            <p className="text-sm text-[var(--text-dim)] mb-3">¿Nueva compañía?</p>
            <Link to="/register">
              <Button
                variant="outline"
                className="w-full rounded-none border-[var(--border)] hover:border-[var(--accent)]"
                data-testid="link-register"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Crear tu AI Company
              </Button>
            </Link>
            <Link to="/precios">
              <button className="mt-3 text-xs text-[var(--text-dim)] hover:text-[var(--accent)] underline"
                      data-testid="link-pricing">
                Ver planes y precios
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
